'use client'

// paddle-michigan homepage — interactive Michigan map + river card
// list with bidirectional hover binding.
//
// Client component (it owns the shared `hoveredRiverId` state). The
// data is fetched server-side in app/pm/home/page.tsx and handed in
// as the `rivers` prop.
//
// ⚠ The sidebar card JSX below is a v1 DUPLICATE of the per-river
// card in app/state/[state]/page.tsx — extracting a shared
// <StateRiverCard> would require editing that file, which the
// kickoff hard-constraint locks as byte-identical. Every delta from
// the /state/[state] card is tagged `[PM-DUPE]`. v2 dedup is filed
// in TODO.md (same soak gate as the /pm/rivers dedup item).
// See PLAN-PM-MAP.md §5 / O-1.

import { useState } from 'react'
import Link from 'next/link'
import type { FlowCondition } from '@/types'
import { getDesignationBadges } from '@/lib/designations'
import StateRiverMap from '@/components/maps/StateRiverMap'
import { STATE_MAP_CONFIG } from '@/data/state-centers'
import { PADDLE_MICHIGAN_BRAND } from '@/lib/paddle-michigan-config'

/** River view-model. Superset of StateRiverMap's RiverDot (so it can
 *  be passed straight through) plus the sidebar-card fields. Built
 *  server-side in app/pm/home/page.tsx; fully serializable. */
export interface PMRiverVM {
  // RiverDot fields — consumed by StateRiverMap unchanged:
  id: string
  name: string
  lat: number
  lng: number
  condition: FlowCondition
  cfs: number | null
  cls: string
  stateKey: string
  // sidebar-card fields:
  co: string
  len: string
  desig: string   // '' when the river has no designation (→ no badges)
  path: string
  changePerHour: number | null
  trend: string | null
  rateLabel: string | null
  hasRapids: boolean
  hasPermit: boolean
}

// [PM-DUPE] COND_COLOR — duplicated verbatim from app/state/[state]/
// page.tsx. Sidebar CFS/condition accent colors.
const COND_COLOR: Record<string, string> = {
  optimal: 'var(--rv)',
  low:     '#b08050',
  high:    '#1873cc',
  flood:   '#c0392b',
  loading: 'var(--tx3)',
}

export default function MichiganMapView({ rivers }: { rivers: PMRiverVM[] }) {
  // IMPORTANT: rivers MUST remain reference-stable across re-renders.
  // StateRiverMap's map-building useEffect includes rivers in its deps
  // array, so a fresh array reference would rebuild the entire Mapbox
  // map on every hover. This component receives rivers from the server
  // component once and passes it through unchanged — do NOT
  // destructure, filter, or .map() it here (mapping for the sidebar
  // JSX below is fine; only the `rivers` prop handed to StateRiverMap
  // must keep its identity, which pass-through guarantees).
  const [hoveredRiverId, setHoveredRiverId] = useState<string | null>(null)

  const mi = STATE_MAP_CONFIG['mi']

  return (
    // [PM-DUPE] `.state-layout` reused verbatim. `.pm-map-view` is an
    // additive class whose ONLY rule (app/globals.css) flips the map
    // above the list on mobile (O-2 ruling) without touching the
    // shared `.state-layout` mobile rules.
    <main style={{ minHeight: '100vh', background: 'var(--bg)', color: 'var(--tx)' }}>
      <div className="state-layout pm-map-view">
        {/* Sidebar — river card list */}
        <div className="state-sidebar">
          <div style={{ padding: '10px 12px', borderBottom: '1px solid var(--bd)' }}>
            <div style={{ fontFamily: "'Playfair Display', serif", fontSize: '14px', fontWeight: 700, marginBottom: '3px' }}>
              Michigan
            </div>
            <div style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: '11px', color: 'var(--tx3)' }}>
              {rivers.length} rivers · Live USGS data
            </div>
          </div>

          {rivers.map(river => {
            const condColor = COND_COLOR[river.condition ?? 'loading']
            const isHovered = hoveredRiverId === river.id
            const badges = getDesignationBadges(river.desig)
            return (
              <Link
                key={river.id}
                href={river.path}
                // [PM-DUPE] hover handlers + hovered background — the
                // ONE behavioral delta from the /state/[state] card.
                onMouseEnter={() => setHoveredRiverId(river.id)}
                onMouseLeave={() => setHoveredRiverId(null)}
                style={{
                  display: 'block', padding: '9px 12px',
                  borderBottom: '1px solid var(--bd)',
                  textDecoration: 'none', color: 'var(--tx)',
                  background: isHovered ? 'var(--bg2)' : undefined,
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '5px', fontFamily: "'Playfair Display', serif", fontSize: '13px', fontWeight: 600, marginBottom: '2px' }}>
                    {river.name}
                    {river.hasRapids && (
                      <span className="verified-badge" title="Rapids verified by local paddlers">&#10003;</span>
                    )}
                  </div>
                  {river.cfs !== null && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '4px', flexShrink: 0, marginLeft: '6px' }}>
                      <div style={{ width: '5px', height: '5px', borderRadius: '50%', background: condColor, flexShrink: 0 }} />
                      <span style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: '11px', fontWeight: 500, color: condColor }}>
                        {/* [PM-DUPE] cfs.toLocaleString() instead of
                            /state's formatCfs() — avoids importing
                            lib/usgs into the client bundle; same
                            numeric output. Matches what StateRiverMap's
                            own tooltip already does client-side. */}
                        {river.cfs.toLocaleString()}
                      </span>
                      <span style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: '11px', color: 'var(--tx3)' }}>
                        cfs
                      </span>
                      {river.changePerHour !== null && Math.abs(river.changePerHour) >= 25 && (
                        <span
                          title={river.rateLabel ?? undefined}
                          style={{
                            fontFamily: "'IBM Plex Mono', monospace",
                            fontSize: '11px',
                            color: river.trend === 'up' ? 'var(--am)' : 'var(--wt)',
                            fontWeight: 500,
                          }}>
                          {river.trend === 'up' ? '↑' : '↓'}{Math.abs(river.changePerHour) > 100 ? Math.abs(river.changePerHour) > 300 ? '!!' : '!' : ''}
                        </span>
                      )}
                    </div>
                  )}
                </div>
                <div style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: '11px', color: 'var(--tx2)', marginBottom: '4px' }}>
                  {river.co}
                </div>
                <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap', alignItems: 'center' }}>
                  <span style={{ fontSize: '11px', fontFamily: "'IBM Plex Mono', monospace", padding: '2px 5px', borderRadius: '3px', background: 'var(--wtlt)', color: 'var(--wt)' }}>
                    Class {river.cls}
                  </span>
                  <span style={{ fontSize: '11px', fontFamily: "'IBM Plex Mono', monospace", color: 'var(--tx3)' }}>
                    {river.len}
                  </span>
                  {river.condition !== 'loading' && (
                    <span style={{ fontSize: '11px', fontFamily: "'IBM Plex Mono', monospace", padding: '2px 5px', borderRadius: '3px', color: condColor, background: 'var(--bg2)', border: `1px solid ${condColor}`, opacity: 0.85 }}>
                      {river.condition === 'optimal' ? 'Optimal' : river.condition === 'low' ? 'Low' : river.condition === 'high' ? 'High' : 'Flood'}
                    </span>
                  )}
                  {river.hasPermit && (
                    <span
                      title="Permit required — see river page for details."
                      style={{
                        fontSize: '11px', fontFamily: "'IBM Plex Mono', monospace",
                        padding: '2px 5px', borderRadius: '3px',
                        color: '#7A4D0E', background: 'var(--amlt)',
                        border: '1px solid var(--am)',
                        fontWeight: 600, letterSpacing: '.3px',
                      }}>
                      PERMIT
                    </span>
                  )}
                </div>
                {badges.length > 0 && (
                  <div style={{ display: 'flex', gap: '3px', flexWrap: 'wrap', marginTop: '3px' }}>
                    {badges.slice(0, 2).map((b, i) => (
                      <span key={i} style={{
                        fontFamily: "'IBM Plex Mono', monospace", fontSize: '9px',
                        padding: '1px 5px', borderRadius: '6px',
                        color: b.color, background: b.bg, border: `1px solid ${b.border}`,
                        display: 'inline-flex', alignItems: 'center', gap: '2px',
                      }}>
                        <span style={{ fontSize: '9px' }}>{b.icon}</span> {b.label}
                      </span>
                    ))}
                  </div>
                )}
              </Link>
            )
          })}
        </div>

        {/* Main panel — interactive Michigan map */}
        <div className="state-map">
          <StateRiverMap
            // Pass-through — reference-stable (see top-of-component note).
            rivers={rivers}
            stateName="Michigan"
            stateCenter={mi.center}
            stateZoom={mi.zoom}
            // Bidirectional hover binding (additive props):
            externallyHoveredId={hoveredRiverId}
            onHoverChange={setHoveredRiverId}
            // paddle-michigan-branded river labels (O-4 ruling).
            labelColor={PADDLE_MICHIGAN_BRAND.mapLabelColor}
          />
        </div>
      </div>
    </main>
  )
}
